/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Skeleton,
  Tab,
  Tabs,
  Paper,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  Category as CategoryIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Palette as PaletteIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import EnhancedTemplateEditor from './EnhancedTemplateEditor';
import VariableManager from './VariableManager';
import MultiPageEditor from './MultiPageEditor';
import EntityBrandingManager from './EntityBrandingManager';
import { useEntityContext } from '../EntityContextSwitch';
import TemplatePreview from './TemplatePreview';
import { useTemplateCategories } from '../../hooks/useTemplateCategories';

// eslint-disable-next-line max-lines-per-function
export default function HtmlTemplateManagement() {
  useAuth();
  const { currentEntity } = useEntityContext();
  const { categories } = useTemplateCategories();

  // Debug: Log the currentEntity object structure
  console.log('🔍 currentEntity object:', currentEntity);

  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateContent, setTemplateContent] = useState('');
  const [multiPageData, setMultiPageData] = useState(null);
  const [useMultiPage, setUseMultiPage] = useState(false);
  const [showVariablesSidebar, setShowVariablesSidebar] = useState(true);
  const [lockStatus, setLockStatus] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
    categoryId: 'general',
    tags: [],
    isGlobal: false,
    status: 'draft',
    locked: false
  });

  // Variables state
  const [templateVariables, setTemplateVariables] = useState([]);
  const [customVariables, setCustomVariables] = useState([]);

  // Branding state
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [entityBranding, setEntityBranding] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    console.log('⚡ useEffect triggered - activeTab:', activeTab, 'entityId:', currentEntity?.id);

    // Clear templates when switching tabs to force re-render
    setTemplates([]);
    setLoading(true);

    // Fetch templates based on active tab
    if (activeTab === 1) {
      console.log('📍 Condition: Global Templates (activeTab === 1)');
      fetchTemplates();
    } else if (activeTab === 0 && currentEntity?.id) {
      console.log('📍 Condition: Entity Templates (activeTab === 0 AND entity exists)');
      fetchTemplates();
    } else {
      console.log('📍 Condition: No fetch - activeTab:', activeTab, 'entityId:', currentEntity?.id);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentEntity?.id]);

  const fetchTemplates = async () => {
    console.log('🚀 fetchTemplates called - activeTab at execution:', activeTab);

    try {
      const filters = activeTab === 1
        ? { isGlobal: true }
        : { entityId: currentEntity?.id };

      console.log('🔍 Building filters - activeTab:', activeTab, '| filters:', filters);
      const response = await api.listTemplates(filters);
      console.log('📦 Templates response:', response);

      // Backend always returns global templates when entityId is provided
      // For Entity Templates tab, filter out global templates client-side
      let filteredTemplates = response.templates || [];
      if (activeTab === 0) {
        console.log('🔧 Filtering for Entity Templates only');
        filteredTemplates = filteredTemplates.filter(t => !t.isGlobal && t.entityId === currentEntity?.id);
      }

      console.log('📦 Final filtered templates:', filteredTemplates);
      setTemplates(filteredTemplates);
    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      showSnackbar('Failed to fetch templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    console.log('🎯 Tab changing from', activeTab, 'to', newValue);
    setActiveTab(newValue);
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateData({
      name: '',
      description: '',
      categoryId: 'general',
      tags: [],
      isGlobal: activeTab === 1,
      status: 'draft',
      locked: false
    });
    setTemplateContent('');
    setMultiPageData(null);
    setUseMultiPage(false);
    setTemplateVariables([]);
    setCustomVariables([]);
    setEditorOpen(true);
  };

  const handleEditTemplate = async (template) => {
    try {
      const response = await api.getTemplate(template.id);
      setEditingTemplate(response.template);
      setTemplateData({
        name: response.template.name,
        description: response.template.description,
        categoryId: response.template.categoryId || response.template.templateType || 'general',
        tags: response.template.tags || [],
        isGlobal: response.template.isGlobal,
        status: response.template.status,
        locked: response.template.locked || false
      });
      setTemplateContent(response.template.htmlContent || '');
      if (response.template.pages && response.template.pages.length > 0) {
        setUseMultiPage(true);
        setMultiPageData({
          pages: response.template.pages,
          headerContent: response.template.headerContent,
          footerContent: response.template.footerContent,
          pageSettings: response.template.pageSettings
        });
      }
      setTemplateVariables(response.template.variables || []);
      setCustomVariables(response.template.customVariables || []);
      setEditorOpen(true);

      // Check lock status for existing template
      checkLockStatus(response.template.id);
    } catch (error) {
      console.error('Error fetching template details:', error);
      showSnackbar('Failed to load template', 'error');
    }
  };

  // Lock management functions
  const checkLockStatus = async (templateId) => {
    if (!templateId) return;

    try {
      const status = await api.getTemplateLockStatus(templateId);
      setLockStatus(status);
    } catch (err) {
      console.error('Failed to check lock status:', err);
    }
  };

  const handleAcquireLock = async () => {
    if (!editingTemplate?.id) return;

    try {
      await api.acquireTemplateLock(editingTemplate.id);
      await checkLockStatus(editingTemplate.id);
      showSnackbar('Template locked for editing', 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to acquire lock', 'error');
    }
  };

  const handleReleaseLock = async () => {
    if (!editingTemplate?.id) return;

    try {
      await api.releaseTemplateLock(editingTemplate.id);
      await checkLockStatus(editingTemplate.id);
      showSnackbar('Template lock released', 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to release lock', 'error');
    }
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateData.name) {
      showSnackbar('Please provide a template name', 'error');
      return;
    }

    try {
      const payload = {
        ...templateData,
        entityId: activeTab === 1 ? null : currentEntity?.id,
        htmlContent: useMultiPage ? multiPageData?.htmlContent : templateContent,
        variables: templateVariables,
        customVariables: customVariables
      };

      // Add multi-page data if enabled
      if (useMultiPage && multiPageData) {
        payload.pages = multiPageData.pages;
        payload.headerContent = multiPageData.headerContent;
        payload.footerContent = multiPageData.footerContent;
        payload.pageSettings = multiPageData.pageSettings;
      }

      if (editingTemplate) {
        await api.updateTemplate({
          templateId: editingTemplate.id,
          ...payload
        });
        showSnackbar('Template updated successfully', 'success');
      } else {
        await api.createTemplate(payload);
        showSnackbar('Template created successfully', 'success');
      }

      setEditorOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      showSnackbar('Failed to save template', 'error');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await api.deleteTemplate(template.id);
      showSnackbar('Template deleted successfully', 'success');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showSnackbar('Failed to delete template', 'error');
    }
  };

  const handleVariableInsert = (variable) => {
    // Add to template variables if not already present
    if (!templateVariables.find(v => v.name === variable.name)) {
      setTemplateVariables([...templateVariables, variable]);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const isLocked = lockStatus?.isLocked && !lockStatus?.isLockedByMe;

  const renderTemplateCard = (template) => {
    const category = categories.find(c => c.id === (template.categoryId || template.templateType));

    return (
      <Card key={template.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 0 }}>
                {template.name}
              </Typography>
              {template.locked && (
                <Tooltip title="Template is locked - only creator and admins can edit">
                  <Typography component="span" sx={{ fontSize: '1.2rem' }}>
                    🔒
                  </Typography>
                </Tooltip>
              )}
            </Box>
            <Chip
              label={template.status}
              size="small"
              color={template.status === 'published' ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>

          {category && (
            <Chip
              icon={<CategoryIcon />}
              label={category.name}
              size="small"
              sx={{
                mb: 1,
                backgroundColor: category.color + '20',
                color: category.color
              }}
            />
          )}

          {template.description && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {template.description}
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            {template.variables?.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block">
                Variables: {template.variables.map(v => v.name).join(', ')}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              Version: {template.currentVersion || '1.0.0'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Updated: {new Date(template.updatedAt || template.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        </CardContent>

        <CardActions>
          <Tooltip title="Edit Template">
            <IconButton size="small" onClick={() => handleEditTemplate(template)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate Template">
            <IconButton size="small">
              <CopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share Template">
            <IconButton size="small">
              <ShareIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Entity Branding">
            <IconButton size="small" onClick={() => setBrandingOpen(true)}>
              <PaletteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Template">
            <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(template)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Template Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTemplate}
          >
            Create Template
          </Button>
        </Box>

        <Paper sx={{ mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Entity Templates" />
            <Tab label="Global Templates" />
          </Tabs>
        </Paper>
      </Box>
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3].map(i => (
            <Grid
              key={i}
              size={{
                xs: 12,
                md: 6,
                lg: 4
              }}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      ) : templates.length === 0 ? (
        <Alert severity="info">
          No templates found. Create your first template to get started.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {templates.map(template => (
            <Grid
              key={template.id}
              size={{
                xs: 12,
                md: 6,
                lg: 4
              }}>
              {renderTemplateCard(template)}
            </Grid>
          ))}
        </Grid>
      )}
      {/* Template Editor Dialog */}
      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6">
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Build beautiful documents with dynamic content
              </Typography>
            </Box>
            {editingTemplate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {lockStatus && (
                  <Chip
                    icon={lockStatus.isLockedByMe ? <LockOpenIcon /> : <LockIcon />}
                    label={lockStatus.isLockedByMe ? 'Locked by you' : `Locked by ${lockStatus.lockedByEmail}`}
                    color={lockStatus.isLockedByMe ? 'success' : 'warning'}
                    size="small"
                    onDelete={lockStatus.isLockedByMe ? handleReleaseLock : undefined}
                    deleteIcon={<CloseIcon />}
                  />
                )}

                {!lockStatus?.isLocked && (
                  <Button
                    size="small"
                    startIcon={<LockIcon />}
                    onClick={handleAcquireLock}
                    variant="outlined"
                  >
                    Lock for Editing
                  </Button>
                )}

                <IconButton size="small" onClick={() => handlePreviewTemplate({...templateData, htmlContent: templateContent})}>
                  <PreviewIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          overflow: 'auto',
          pb: 2,
          pt: 2
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'min-content'
          }}>
          <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'grey.50',
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }
              }}
            >
              <SettingsIcon fontSize="small" />
              <Typography>Template Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateData.name}
                onChange={(e) => setTemplateData({ ...templateData, name: e.target.value })}
                required
                disabled={isLocked}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={templateData.categoryId}
                  onChange={(e) => setTemplateData({ ...templateData, categoryId: e.target.value })}
                  label="Category"
                  disabled={isLocked}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={templateData.status}
                  onChange={(e) => setTemplateData({ ...templateData, status: e.target.value })}
                  label="Status"
                  disabled={isLocked}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={templateData.description}
                onChange={(e) => setTemplateData({ ...templateData, description: e.target.value })}
                disabled={isLocked}
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={templateData.tags}
                onChange={(e, value) => setTemplateData({ ...templateData, tags: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add tags..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} />
                  ))
                }
              />
            </Grid>
            {activeTab === 0 && (
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={templateData.isGlobal}
                      onChange={(e) => setTemplateData({ ...templateData, isGlobal: e.target.checked })}
                    />
                  }
                  label="Make this template available to all entities"
                />
              </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useMultiPage}
                    onChange={(e) => setUseMultiPage(e.target.checked)}
                  />
                }
                label="Enable multi-page template"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={templateData.locked}
                    onChange={(e) => setTemplateData({ ...templateData, locked: e.target.checked })}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🔒 Lock template
                    <Typography variant="caption" color="text.secondary">
                      (Only you and admins can edit)
                    </Typography>
                  </Box>
                }
              />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded={false} sx={{ mb: 2, flexShrink: 0 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'primary.dark',
                color: 'primary.contrastText',
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  color: 'primary.contrastText'
                }
              }}
            >
              <Typography>💡 How Templates Work</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
              <Typography variant="body2">
                Templates are smart documents that automatically fill in information. Use the <strong>Insert Variable</strong> button (🔧) in the editor toolbar or click variables in the sidebar to insert them at your cursor position. Variables look like <strong>{'{{variableName}}'}</strong> and will be replaced with real data when generating reports. The AI will interpret these variables based on your entity's data and context.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Box sx={{
            display: 'flex',
            gap: 2,
            minHeight: '400px'
          }}>
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                flexShrink: 0
              }}>
                <Typography variant="h6">
                  ✏️ Template Content
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Write your document and insert variables where you want dynamic content
                </Typography>
              </Box>
              <Box sx={{ minHeight: '300px' }}>
                {useMultiPage ? (
                  <MultiPageEditor
                    template={multiPageData || { htmlContent: templateContent }}
                    onChange={(data) => setMultiPageData(data)}
                    variables={templateVariables}
                    customVariables={customVariables}
                    onVariableInsert={handleVariableInsert}
                    entityBranding={entityBranding}
                  />
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    {lockStatus && (
                      <Box sx={{
                        position: 'absolute',
                        top: -40,
                        right: 0,
                        zIndex: 1,
                        display: 'flex',
                        gap: 1
                      }}>
                        <Chip
                          size="small"
                          icon={lockStatus.isLockedByMe ? <LockOpenIcon /> : (lockStatus.isLocked ? <LockIcon /> : null)}
                          label={
                            lockStatus.isLockedByMe ? 'You have edit lock' :
                            lockStatus.isLocked ? `Locked by ${lockStatus.lockedByEmail}` :
                            'No lock - anyone can edit'
                          }
                          color={lockStatus.isLockedByMe ? 'success' : (lockStatus.isLocked ? 'warning' : 'default')}
                          variant="outlined"
                        />
                        {!lockStatus.isLocked && editingTemplate && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LockIcon />}
                            onClick={handleAcquireLock}
                          >
                            Acquire Lock
                          </Button>
                        )}
                        {lockStatus.isLockedByMe && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<LockOpenIcon />}
                            onClick={handleReleaseLock}
                          >
                            Release Lock
                          </Button>
                        )}
                      </Box>
                    )}
                    <EnhancedTemplateEditor
                      initialContent={templateContent}
                      onChange={setTemplateContent}
                      variables={[...templateVariables, ...customVariables]}
                      onVariableInsert={handleVariableInsert}
                      isLocked={isLocked}
                    />
                    {/* Variable Usage Indicator */}
                    <Box sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'grey.300'
                    }}>
                      <Typography variant="subtitle2" gutterBottom>
                        📊 Variables in Template
                      </Typography>
                      {(() => {
                        // Extract variables from template content
                        const variablePattern = /\{\{(\w+)\}\}/g;
                        const foundVariables = [];
                        let match;
                        while ((match = variablePattern.exec(templateContent)) !== null) {
                          if (!foundVariables.includes(match[1])) {
                            foundVariables.push(match[1]);
                          }
                        }

                        if (foundVariables.length === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              No variables detected in template. Add variables to make your template dynamic!
                            </Typography>
                          );
                        }

                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {foundVariables.map(varName => {
                              const varDef = [...templateVariables, ...customVariables].find(v => v.name === varName);
                              return (
                                <Chip
                                  key={varName}
                                  label={
                                    <Box>
                                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                        {`{{${varName}}}`}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', fontStyle: 'italic' }}>
                                        {varDef?.type === 'entity_field'
                                          ? `AI will pull ${varName} from entity data`
                                          : varDef?.type === 'system'
                                          ? `System will provide ${varName}`
                                          : varDef
                                          ? varDef.description || 'Custom variable'
                                          : 'Undefined - AI will interpret contextually'
                                        }
                                      </Typography>
                                    </Box>
                                  }
                                  size="small"
                                  variant="outlined"
                                  color={varDef ? 'primary' : 'warning'}
                                />
                              );
                            })}
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{
              width: showVariablesSidebar ? 350 : 40,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flexShrink: 0,
              transition: 'width 0.3s ease',
              borderLeft: 1,
              borderColor: 'divider',
              position: 'relative'
            }}>
              {showVariablesSidebar ? (
                <>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    p: 2,
                    pb: 0,
                    flexShrink: 0
                  }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      🔧 Variables
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowVariablesSidebar(false)}
                      sx={{ ml: 'auto' }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                  <Divider />
                  <Box sx={{ flex: 1, overflow: 'hidden', px: 2 }}>
                    <VariableManager
                      variables={templateVariables}
                      customVariables={customVariables}
                      onChange={(vars, custom) => {
                        setTemplateVariables(vars);
                        setCustomVariables(custom);
                      }}
                      onVariableInsert={handleVariableInsert}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  pt: 2
                }}>
                  <IconButton
                    onClick={() => setShowVariablesSidebar(true)}
                    sx={{
                      transform: 'rotate(90deg)',
                      mb: 2
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      color: 'text.secondary'
                    }}
                  >
                    Variables
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          flexShrink: 0,
          borderTop: 1,
          borderColor: 'divider',
          p: 2
        }}>
          <Button onClick={() => setEditorOpen(false)} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            startIcon={<SaveIcon />}
            size="large"
          >
            {editingTemplate ? 'Update' : 'Create'} Template
          </Button>
        </DialogActions>
      </Dialog>
      {/* Entity Branding Dialog */}
      <Dialog
        open={brandingOpen}
        onClose={() => setBrandingOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Entity Branding Settings
        </DialogTitle>
        <DialogContent>
          <EntityBrandingManager
            entityId={currentEntity?.id}
            entityName={currentEntity?.name}
            onSave={(branding) => {
              setEntityBranding(branding);
              showSnackbar('Branding saved successfully', 'success');
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrandingOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Template Preview Dialog */}
      {previewOpen && previewTemplate && (
        <TemplatePreview
          open={previewOpen}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewTemplate(null);
          }}
          template={previewTemplate}
          variables={{}}
          showVariableEditor={true}
        />
      )}
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}